import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

function upsertMeta(name: string, content: string, attrName: string) {
  let el = document.querySelector(`meta[${attrName}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  jsonLd,
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    upsertMeta('description', description, 'name');
    if (keywords) upsertMeta('keywords', keywords, 'name');

    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type', ogType, 'property');
    upsertMeta('og:site_name', '吃瓜爆料网', 'property');
    upsertMeta('og:locale', 'zh_CN', 'property');
    if (canonicalUrl) upsertMeta('og:url', canonicalUrl, 'property');
    if (ogImage) {
      upsertMeta('og:image', ogImage, 'property');
      upsertMeta('og:image:width', '1200', 'property');
      upsertMeta('og:image:height', '630', 'property');
    }

    upsertMeta('twitter:card', 'summary_large_image', 'name');
    upsertMeta('twitter:title', title, 'name');
    upsertMeta('twitter:description', description, 'name');
    if (ogImage) upsertMeta('twitter:image', ogImage, 'name');

    if (canonicalUrl) upsertLink('canonical', canonicalUrl);

    if (jsonLd) {
      const scriptId = 'json-ld-structured-data';
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      const script = document.getElementById('json-ld-structured-data');
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, jsonLd]);

  return null;
}